import type { City } from './city';
import type { Vec } from '../destruction/structure';
import { wetGround } from './terrain';

/** The same finite river footprint used by world authoring. A background ground
 * collider under water does not make a valid pedestrian surface. */
export function inRiver(city: City, p: Pick<Vec, 'x' | 'z'>, margin = 0): boolean {
  if (city.terrain) return wetGround(city, p.x, p.z, margin);
  const river = city.river;
  if (!river || river.points.length < 2) return false;
  const first = river.points[0],
    second = river.points[1],
    last = river.points.at(-1)!,
    before = river.points.at(-2)!;
  if (
    (p.x - first.x) * (second.x - first.x) + (p.z - first.z) * (second.z - first.z) < 0 ||
    (p.x - last.x) * (last.x - before.x) + (p.z - last.z) * (last.z - before.z) > 0
  )
    return false;
  for (let i = 1; i < river.points.length; i++) {
    const a = river.points[i - 1],
      b = river.points[i],
      dx = b.x - a.x,
      dz = b.z - a.z,
      length = dx * dx + dz * dz;
    if (!length) continue;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / length));
    if (Math.hypot(p.x - a.x - dx * t, p.z - a.z - dz * t) < river.width / 2 + margin) return true;
  }
  return false;
}

/** Read-only road strip index for access planning. Grade-separated paths remain
 * distinct; only ground-level traffic at the sampled height closes access. */
export class TrafficSurface {
  private bins = new Map<string, { a: Vec; b: Vec; width: number }[]>();
  constructor(city: City) {
    for (const e of city.roadGraph?.edges ?? [])
      for (let i = 1; i < (e.profile?.length ?? 0); i++) {
        const strip = { a: e.profile![i - 1], b: e.profile![i], width: e.width };
        for (
          let x = Math.floor((Math.min(strip.a.x, strip.b.x) - e.width) / 32);
          x <= Math.floor((Math.max(strip.a.x, strip.b.x) + e.width) / 32);
          x++
        )
          for (
            let z = Math.floor((Math.min(strip.a.z, strip.b.z) - e.width) / 32);
            z <= Math.floor((Math.max(strip.a.z, strip.b.z) + e.width) / 32);
            z++
          ) {
            const key = `${x},${z}`,
              list = this.bins.get(key) ?? [];
            list.push(strip);
            this.bins.set(key, list);
          }
      }
  }
  contains(p: Vec, inset = 0): boolean {
    for (const { a, b, width } of this.bins.get(`${Math.floor(p.x / 32)},${Math.floor(p.z / 32)}`) ?? []) {
      const dx = b.x - a.x,
        dz = b.z - a.z,
        len = Math.hypot(dx, dz),
        t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / (len * len);
      if (
        t >= 0 &&
        t <= 1 &&
        Math.abs(p.y - a.y - (b.y - a.y) * t) < 0.55 &&
        Math.abs((p.z - a.z) * dx - (p.x - a.x) * dz) / len < width / 2 - inset
      )
        return true;
    }
    return false;
  }
  clear(a: Vec, b: Vec): boolean {
    const steps = Math.max(1, Math.ceil(Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) / 0.65));
    for (let i = 0; i <= steps; i++)
      if (
        this.contains(
          {
            x: a.x + ((b.x - a.x) * i) / steps,
            y: a.y + ((b.y - a.y) * i) / steps,
            z: a.z + ((b.z - a.z) * i) / steps,
          },
          0.1,
        )
      )
        return false;
    return true;
  }
}
